import { SenderRecipientOption } from "../components/SenderRecipientDropdown";

import { Draft } from "./inboxDraft.types";

/**
 * Keeps each draft's stored sender label in sync with the canonical sender
 * options. The options recompute when the LinkedIn connection check completes
 * (e.g. "… — will send invite" → "(LinkedIn)"), but a draft's `from.label` is
 * captured once at build time and would otherwise stay stale on the selected
 * sender trigger while the dropdown list shows the fresh label.
 *
 * Matches by sender identity (type + user + email) so a user-chosen sender keeps
 * its identity and only its display label is refreshed. Returns the same array
 * reference when nothing changed, so it is safe to call from an effect without
 * causing render loops.
 */
export const refreshDraftSenderLabels = (
	drafts: Draft[],
	senderOptions: SenderRecipientOption[]
): Draft[] => {
	let changed = false;

	const next = drafts.map((draft) => {
		const { from } = draft;

		if (!from) {
			return draft;
		}

		const option = senderOptions.find(
			(candidate) =>
				candidate.type === from.type &&
				candidate.userId === from.userId &&
				(from.type !== "email" || candidate.emailId === from.emailId)
		);

		if (!option || option.label === from.label) {
			return draft;
		}

		changed = true;

		return { ...draft, from: { ...from, label: option.label } };
	});

	return changed ? next : drafts;
};
